import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSyncStatus } from '../sync/useSyncStatus';
import styles from './SyncStatusIndicator.module.css';
import { getCurrentAccount } from '../accounts/accountService';
import { Wifi, WifiOff, RefreshCw, AlertCircle, Database } from 'lucide-react';

/**
 * Animated sync status indicator for the top navigation bar.
 * Shows connectivity and sync status with minimum 3-second animations.
 * Clicking navigates to sync and device settings.
 */
export const SyncStatusIndicator: React.FC = () => {
  const syncStatus = useSyncStatus();
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayStatus, setDisplayStatus] = useState(syncStatus.status);
  const [animationStartTime, setAnimationStartTime] = useState<number | null>(null);
  const [isAccountless, setIsAccountless] = useState(false);

  useEffect(() => {
    getCurrentAccount().then(acc => {
      setIsAccountless(!!acc?.isAccountless);
    });
  }, []);

  // Handle status changes with minimum 3-second animation duration
  useEffect(() => {
    if (isAccountless) {
      setDisplayStatus('local');
      return;
    }
    const newStatus = syncStatus.status;

    // If syncing starts, begin animation
    if (newStatus === 'syncing' && displayStatus !== 'syncing') {
      setIsAnimating(true);
      setDisplayStatus('syncing');
      setAnimationStartTime(Date.now());
    }
    // If syncing ends, enforce minimum 3-second animation
    else if (displayStatus === 'syncing' && newStatus !== 'syncing') {
      const elapsed = animationStartTime ? Date.now() - animationStartTime : 0;
      const remainingTime = Math.max(0, 3000 - elapsed);

      // Wait for remaining time before transitioning
      const timeoutId = setTimeout(() => {
        setIsAnimating(false);
        setDisplayStatus(newStatus);
        setAnimationStartTime(null);
      }, remainingTime);

      return () => clearTimeout(timeoutId);
    }
    // For other status changes, update immediately
    else if (!isAnimating || displayStatus !== 'syncing') {
      setDisplayStatus(newStatus);
    }
  }, [syncStatus.status, displayStatus, animationStartTime, isAnimating, isAccountless]);

  const getStatusInfo = () => {
    switch (displayStatus) {
      case 'local':
        return {
          icon: <Database className={styles.icon} />,
          label: 'Local Mode',
          className: styles.idle,
          title: 'All data is stored locally. Sync and social features disabled.',
        };
      case 'syncing':
        return {
          icon: <RefreshCw className={styles.icon} />,
          label: 'Syncing',
          className: styles.syncing,
          title: 'Syncing data...',
        };
      case 'offline':
        return {
          icon: <WifiOff className={styles.icon} />,
          label: 'Offline',
          className: styles.offline,
          title: syncStatus.errorMessage || 'Storage node unreachable',
        };
      case 'error':
        return {
          icon: <AlertCircle className={styles.icon} />,
          label: 'Error',
          className: styles.error,
          title: syncStatus.errorMessage || 'Sync error',
        };
      case 'idle':
      default:
        return {
          icon: <Wifi className={styles.icon} />,
          label: 'Online',
          className: styles.idle,
          title: 'Connected and synced',
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <button
      type="button"
      className={`${styles.root} ${statusInfo.className}`}
      onClick={() => navigate('/settings?section=devices')}
      title={`${statusInfo.title} — Click to open sync & device settings`}
      aria-label={`${statusInfo.label} — Sync & device settings`}
    >
      <div className={styles.iconWrapper}>
        {statusInfo.icon}
      </div>
      <span className={styles.label}>{statusInfo.label}</span>
    </button>
  );
};
